import { useState, type FormEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { EffectiveBehavior } from '../../behaviors/index.js'
import type { WorkerTableRow } from '../../roster/index.js'
import { ADMIN_API_BASE_URL, EndpointHint, Skeleton } from '../../core/index.js'
import { listWorkerBehaviorEvents, sendBehaviorEvent, type EventAccepted, type PartnerSession } from '../lib/partnerApi.js'
import { BehaviorEventLog } from '../../scoring/index.js'
import { Send } from 'lucide-react'
import { ErrorNote, INPUT_CLASS, Labeled, SuccessNote, describeError } from './formControls.js'
import { Button } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

/** Now, in the `YYYY-MM-DDTHH:mm` form a datetime-local input uses. */
function nowLocal(): string {
  const now = new Date()
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`
}

interface CustomBehaviorFormProps {
  /** The custom behavior this tab reports, as the chosen facility treats it. */
  behavior: EffectiveBehavior
  session: PartnerSession
  facilityId: string
  workers: WorkerTableRow[]
}

/** Sends `POST /events/behavior` as the partner: one occurrence of a behavior the client defined, for a worker on this roster. */
export function CustomBehaviorForm({ behavior, session, facilityId, workers }: CustomBehaviorFormProps) {
  const queryClient = useQueryClient()
  const [workerId, setWorkerId] = useState('')
  const [occurredAt, setOccurredAt] = useState(nowLocal)
  const [shiftDate, setShiftDate] = useState('')
  const [note, setNote] = useState('')
  const [idempotencyKey, setIdempotencyKey] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [accepted, setAccepted] = useState<EventAccepted | null>(null)

  const eventsKey = ['partner-simulator', 'behavior-events', session.clientId, workerId]
  const eventsQuery = useQuery({
    queryKey: eventsKey,
    queryFn: () => listWorkerBehaviorEvents(ADMIN_API_BASE_URL ?? '', session, workerId),
    enabled: workerId !== '' && Boolean(ADMIN_API_BASE_URL),
  })
  const canSend = workerId !== '' && occurredAt !== '' && !isSending

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (!canSend || !ADMIN_API_BASE_URL) {
      return
    }
    setIsSending(true)
    setError(null)
    setAccepted(null)
    try {
      setAccepted(
        await sendBehaviorEvent(
          ADMIN_API_BASE_URL,
          session,
          {
            facility_id: facilityId,
            worker_id: workerId,
            behavior_key: behavior.key,
            occurred_at: new Date(occurredAt).toISOString(),
            ...(shiftDate === '' ? {} : { shift_date: shiftDate }),
            ...(note.trim() === '' ? {} : { note: note.trim() }),
          },
          idempotencyKey.trim(),
        ),
      )
      await queryClient.invalidateQueries({ queryKey: eventsKey })
    } catch (err) {
      setError(describeError(err, 'The event could not be sent.'))
    } finally {
      setIsSending(false)
    }
  }

  return (
    <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
      <p className="text-xs text-ink-mid">
        <span className="font-bold text-ink">{behavior.label}</span>
        {behavior.description && ` — ${behavior.description}`} It {behavior.direction === 'positive' ? 'counts for' : 'counts against'} the worker.
        {!behavior.enabled && (
          <>
            {' '}
            <span className="font-bold text-coral">This facility has it switched off</span>, so the event is stored but will not count.
          </>
        )}
      </p>
      <div className="grid gap-5 sm:grid-cols-2">
        <Labeled label="Worker" htmlFor="behavior-worker" hint={workers.length === 0 ? 'This facility has no workers on its roster yet.' : undefined}>
          <select
            id="behavior-worker"
            className={INPUT_CLASS}
            value={workerId}
            onChange={(event) => {
              setWorkerId(event.target.value)
              setAccepted(null)
            }}
          >
            <option value="">Choose a worker…</option>
            {workers.map((worker) => (
              <option key={worker.workerId} value={worker.workerId}>
                {worker.name} ({worker.workerId})
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled label="Occurred at" htmlFor="behavior-occurred-at">
          <input id="behavior-occurred-at" type="datetime-local" className={INPUT_CLASS} value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} />
        </Labeled>
        <Labeled label="Shift date" htmlFor="behavior-shift-date" hint="Optional: the shift this relates to.">
          <input id="behavior-shift-date" type="date" className={INPUT_CLASS} value={shiftDate} onChange={(event) => setShiftDate(event.target.value)} />
        </Labeled>
        <Labeled label="Idempotency key" htmlFor="behavior-idempotency-key" hint="Optional. Send the same key twice to see a retry answered with the original event.">
          <input id="behavior-idempotency-key" className={`${INPUT_CLASS} font-mono`} value={idempotencyKey} onChange={(event) => setIdempotencyKey(event.target.value)} autoComplete="off" />
        </Labeled>
        <div className="sm:col-span-2">
          <Labeled label="Note" htmlFor="behavior-note" hint="Optional, up to 500 characters.">
            <input id="behavior-note" className={INPUT_CLASS} value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
          </Labeled>
        </div>
      </div>

      <Button icon={Send} size="lg" type="submit" disabled={!canSend}>
        {isSending ? 'Sending…' : `Send ${behavior.label}`}
      </Button>
      <EndpointHint className="ml-3">
        POST /events/behavior · behavior_key: {behavior.key}
      </EndpointHint>

      {error && <ErrorNote message={error} />}
      {accepted && (
        <SuccessNote>
          {accepted.duplicate ? 'Already on record as ' : 'Stored as '}
          <span className="font-mono">{accepted.eventId}</span>.{accepted.duplicate ? ' The API recognised the idempotency key and stored nothing new.' : ''}
        </SuccessNote>
      )}

      {workerId !== '' && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">
            On record for this worker <EndpointHint>GET /workers/{workerId}/behavior-events</EndpointHint>
          </div>
          {eventsQuery.isPending ? (
            <Skeleton label="Loading events" />
          ) : eventsQuery.isError ? (
            <LoadFailure size="xs" message={describeError(eventsQuery.error, 'Could not load this worker’s behavior events.')} onRetry={() => void eventsQuery.refetch()} retrying={eventsQuery.isFetching} />
          ) : (
            <BehaviorEventLog events={eventsQuery.data} labels={new Map([[behavior.key, behavior.label]])} />
          )}
        </div>
      )}
    </form>
  )
}
